import { useState } from "react";
import { MetricValue } from "@/components/marketing/MetricValue";

/**
 * Generic breakdown table. Columns describe how to read each row so the
 * page never recalculates a value: `{ key, label, format, metric }`.
 * `metric: true` means the cell is a Stage 2 metric object (may be null).
 */
export const BreakdownTable = ({ columns, rows, testid, sortable = [], empty,
                                 onRowClick, footnote }) => {
  const [sort, setSort] = useState(null);
  const [dir, setDir] = useState("desc");
  const sorted = sort
    ? [...rows].sort((a, b) => {
      const av = a[sort] ?? -Infinity;
      const bv = b[sort] ?? -Infinity;
      return dir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    })
    : rows;

  if (!rows.length) {
    return (
      <p data-testid={`${testid}-empty`} className="text-sm text-slate-500 py-6">
        {empty || "No data for the current filters."}
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <table data-testid={testid} className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              {columns.map((c) => (
                <th key={c.key} className="px-3 py-2 whitespace-nowrap font-bold">
                  {sortable.includes(c.key) ? (
                    <button data-testid={`${testid}-sort-${c.key}`}
                      onClick={() => {
                        setDir(sort === c.key && dir === "desc" ? "asc" : "desc");
                        setSort(c.key);
                      }}
                      className="hover:text-slate-900">
                      {c.label}{sort === c.key ? (dir === "desc" ? " ↓" : " ↑") : ""}
                    </button>
                  ) : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map((row, i) => (
              <tr key={row.id || row[columns[0].key] || i}
                data-testid={`${testid}-row-${row[columns[0].key] || i}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`${onRowClick ? "cursor-pointer hover:bg-slate-50" : ""}
                  transition-colors`}>
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-2 whitespace-nowrap text-slate-700">
                    {c.metric
                      ? <MetricValue
                          metric={{ value: row[c.key],
                                    reason: row[c.reasonKey || "roas_reason"] }}
                          format={c.format} />
                      : (c.format ? (row[c.key] === null || row[c.key] === undefined
                          ? "—" : c.format(row[c.key]))
                        : (Array.isArray(row[c.key]) ? row[c.key].join(", ")
                          : (row[c.key] ?? "—")))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footnote && <p className="text-[11px] text-slate-500 px-3">{footnote}</p>}
    </div>
  );
};
